import { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { darken, transparentize } from 'polished';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from '@material-ui/core/Typography';
import StoreIcon from 'mdi-react/StoreIcon';
import PencilIcon from 'mdi-react/PencilIcon';
import ImageIcon from 'mdi-react/ImageIcon';
import CheckIcon from 'mdi-react/CheckIcon';
import type { WWWCompanyDocument } from '../../../common/documents/WWWCompanyDocument';
import { companyEditState } from '../../context/companyEditStore';
import { Crop, getContainedImg, getCroppedImg } from '../../common-react/cropImage';
import { Section, Logo, Row } from '../company-list/CompanyListStyle';
import { sendFile } from '../../lib/utils';
import { useScopedState } from '../../lib/state';
import { Header } from '../Header';
import { Button } from '../Button';
import { Help } from '../Help';
import { CompanyEditDialogContent } from './CompanyEditDialog';
import { CompanyEditImage } from './CompanyEditImage';
import { borderRadius, Color } from '../Theme';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Image = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  margin: 0;
  width: 6rem;
  height: 6rem;
  border-radius: ${borderRadius}px;
  border: dashed 2px ${darken(0.1, Color.background)};
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  overflow: hidden;

  svg {
    color: ${darken(0.2, Color.background)};
  }
`;

const ImageEdit = styled.div`
  position: absolute;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 0.5rem;
  width: 100%;
  height: 100%;
  background: linear-gradient(225deg, ${Color.disabled} 0%, ${transparentize(1, Color.disabled)} 50%);
  box-sizing: border-box;
  cursor: pointer;

  svg {
    color: rgba(0, 0, 0, 0.87);
  }
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0.8rem 0;
  padding: 2rem;
  height: 400px;
  background-color: ${Color.background};
  border-radius: ${borderRadius}px;

  min-height: 284px;

  ${Row} {
    margin-top: 2rem;
    border-radius: ${borderRadius}px !important;

    > div {
      display: flex;
      flex: 1;
      position: relative;
      align-items: center;
      border-radius: inherit;
    }
  }
`;

const PreviewImage = styled.img`
  width: 200px;
  height: 200px;
  background-color: white;
  border-radius: ${borderRadius}px;
`;

/** Simulate the company page hero */
const Hero = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  ${Image} {
    flex: none !important;
  }

  > div {
    flex: 1;
    padding-left: 2rem;

    span:first-child {
      margin-top: -0.5rem;
      margin-bottom: 0.5rem;
      font-size: 2.8rem;
    }
  }
`;

export const CompanyImage: FC<{ company: WWWCompanyDocument; editable: boolean; onEdit?: () => void }> = ({
  company,
  editable,
  onEdit,
}) => {
  const { logo } = company;

  const hasLogo = logo && logo.path;

  let styleProp = {};
  if (hasLogo) {
    styleProp = {
      backgroundImage: `url(/api/v1/files/public/96x96M-${logo.path})`,
      backgroundColor: 'white',
      border: 'none',
    };
  }

  return (
    <Image style={{ ...styleProp }}>
      {!hasLogo && <StoreIcon size={64} />}

      {editable && (
        <ImageEdit onClick={onEdit}>
          <PencilIcon size={18} />
        </ImageEdit>
      )}
    </Image>
  );
};

const convertDataURIToBlob = (dataURI: string) => {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  const byteString = atob(dataURI.split(',')[1]);

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length);

  // create a view into the buffer
  const ia = new Uint8Array(ab);

  // set the bytes of the buffer to the correct values
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  // write the ArrayBuffer to a blob, and you're done
  const blob = new Blob([ab], { type: mimeString });
  return blob;
};

export const CompanyImageEdit: CompanyEditDialogContent = ({ company, disableSave, setControl, setError, close, save }) => {
  const intl = useIntl();

  const title = intl.formatMessage({ id: 'company.imageTitle', defaultMessage: 'Logo' });

  const [step, setStep] = useState('select');
  const [isLoading, setIsLoading] = useState(false);
  const [cropState, setCropState] = useState<{ image: string; crop: Crop }>(null);
  const [image, setImage] = useState<string>(null);
  const { set } = useScopedState(companyEditState, []);

  const handleImageUpload = useCallback(async () => {
    setIsLoading(true);

    const blob = convertDataURIToBlob(image);

    const { status: uploadStatus, fileID, path } = await sendFile(blob, () => {}, '/api/v1/www/file/upload');
    if (uploadStatus !== 'ok' || !fileID) {
      setIsLoading(false);
      return setError(<FormattedMessage id="unknownError" defaultMessage="Jokin meni vikaan, yritä myöhemmin uudelleen…" />);
    }

    set({ logo: { fileID, path } });

    await save();

    setIsLoading(false);
  }, [image]);

  useEffect(() => {
    setControl(
      <>
        <Button disabled={isLoading} variant="text" color="default" onClick={close}>
          <FormattedMessage id="company.edit.cancelButton" defaultMessage="Peruuta" />
        </Button>
        {step !== 'preview' && (
          <Button disabled={!cropState || isLoading} onClick={handleCropComplete}>
            <FormattedMessage id="company.edit.nextButton" defaultMessage="Seuraava" />
          </Button>
        )}
        {step === 'preview' && (
          <Button disabled={!cropState || isLoading} loading={isLoading} endIcon={<CheckIcon />} onClick={handleImageUpload}>
            <FormattedMessage id="company.edit.saveButton" defaultMessage="Tallenna" />
          </Button>
        )}
      </>
    );
  }, [step, cropState, isLoading, handleImageUpload]);

  const handleCropComplete = async () => {
    if (!cropState) return;

    let image = null;
    if (cropState.crop) {
      image = await getCroppedImg(cropState.image, cropState.crop);
    } else {
      image = await getContainedImg(cropState.image);
    }

    setImage(image);
    setStep('preview');
  };

  const handleCropChange = (image: string, crop: Crop) => {
    setCropState({ image, crop });
  };

  // TODO: Cropping state is disabled for now, skip it
  useEffect(() => {
    handleCropComplete();
  }, [cropState]);

  const imgStyle = {
    backgroundImage: `url(${image})`,
    backgroundColor: 'white',
    border: 'none',
  };

  return (
    <Container>
      <Header icon={<ImageIcon />}>{title}</Header>

      {step === 'select' && <CompanyEditImage onComplete={handleCropChange} />}
      {step === 'preview' && (
        <>
          <PreviewContainer>
            {/*<PreviewImage src={image} />*/}

            <Hero>
              <Image style={imgStyle} />

              <div>
                <Skeleton animation={false} width="100%" />
                <Skeleton animation={false} width="40%" />
              </div>
            </Hero>

            <Row small style={{ width: '100%' }}>
              <div>
                <Logo style={imgStyle} />
                <Section flex={true} width={null}>
                  <Typography>
                    <Skeleton animation={false} width="80%" />
                  </Typography>
                  <Typography variant="body2">
                    <Skeleton animation={false} width="30%" />
                  </Typography>
                </Section>
              </div>
            </Row>
          </PreviewContainer>
          <Help>
            <FormattedMessage
              id="company.image.previewInfo"
              defaultMessage="Tarkista, näyttääkö kuva hyvältä. Tässä esimerkit kahdesta yleisimmästä näkymästä mihin logo upotetaan."
            />
          </Help>
        </>
      )}
    </Container>
  );
};
